import * as React from 'react';
import {
    AutocompleteInput,
    DateInput,
    NumberInput,
    SelectInput,
    TextInput,
} from 'react-admin';
import { Box, Typography } from '@mui/material';

/** Odoo-style bold compact labels (shared: product profile, customer, company). */
export const FIELD_LABEL_SX = {
    fontWeight: 700,
    fontSize: '0.8rem',
    color: '#212529',
    minWidth: 140,
    lineHeight: '30px',
};

export const FIELD_VALUE_SX = {
    fontSize: '0.8rem',
    color: '#212529',
    lineHeight: '30px',
};

/** Bottom border only — `variant="standard"` compact inputs */
export const UNDERLINE_FIELD_SX = {
    mb: 0,
    '& .MuiFormHelperText-root': { display: 'none' },
    '& .MuiInputBase-root': { fontSize: '0.8rem', minHeight: 30 },
    '& .MuiInputBase-input': { py: '5px' },
    '& .MuiInputLabel-root': { display: 'none' },
};

const FIELD_LABEL_COL_PX = 220;
const FIELD_LABEL_COL_PX_XS = 140;

export function FieldRow({
    label,
    children,
    alignItems = 'center',
    dense = false,
}: {
    label: React.ReactNode;
    children: React.ReactNode;
    alignItems?: 'center' | 'flex-start';
    dense?: boolean;
}) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems,
                minHeight: dense ? 28 : 34,
                mb: dense ? '2px' : '4px',
            }}
        >
            <Typography
                component="div"
                sx={{
                    ...FIELD_LABEL_SX,
                    flex: { xs: `0 0 ${FIELD_LABEL_COL_PX_XS}px`, sm: `0 0 ${FIELD_LABEL_COL_PX}px` },
                    width: { xs: FIELD_LABEL_COL_PX_XS, sm: FIELD_LABEL_COL_PX },
                    maxWidth: { xs: FIELD_LABEL_COL_PX_XS, sm: FIELD_LABEL_COL_PX },
                    minWidth: 0,
                    pr: 2,
                    boxSizing: 'border-box',
                    alignSelf: alignItems === 'flex-start' ? 'flex-start' : 'center',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    lineHeight: 1.3,
                    py: dense ? '0px' : '2px',
                    fontSize: dense ? '0.78rem' : FIELD_LABEL_SX.fontSize,
                }}
            >
                {label}
            </Typography>
            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    width: '100%',
                    ...FIELD_VALUE_SX,
                    lineHeight: dense ? '26px' : FIELD_VALUE_SX.lineHeight,
                    '& .RaReferenceInput-root': { width: '100%', display: 'block' },
                    '& .ra-input': { width: '100%', maxWidth: '100%' },
                    '& .MuiFormControl-root': { width: '100%', maxWidth: '100%' },
                    '& .MuiAutocomplete-root': { width: '100%', maxWidth: '100%' },
                    '& .MuiTextField-root': { width: '100%', maxWidth: '100%' },
                }}
            >
                {children}
            </Box>
        </Box>
    );
}

export const CompactTextInput = (props: React.ComponentProps<typeof TextInput>) => (
    <TextInput
        {...props}
        variant="standard"
        margin="none"
        size="small"
        sx={{
            ...UNDERLINE_FIELD_SX,
            ...(props.sx as object),
        }}
    />
);

export const CompactNumberInput = (props: React.ComponentProps<typeof NumberInput>) => (
    <NumberInput
        {...props}
        variant="standard"
        margin="none"
        size="small"
        sx={{
            ...UNDERLINE_FIELD_SX,
            ...(props.sx as object),
        }}
    />
);

export const CompactSelectInput = (props: React.ComponentProps<typeof SelectInput>) => (
    <SelectInput
        {...props}
        variant="standard"
        margin="none"
        size="small"
        sx={{
            ...UNDERLINE_FIELD_SX,
            '& .MuiSelect-select': { py: '5px' },
            '& .MuiSelect-icon': { top: 'calc(50% - 12px)' },
            ...(props.sx as object),
        }}
    />
);

export const CompactDateInput = (props: React.ComponentProps<typeof DateInput>) => (
    <DateInput
        {...props}
        variant="standard"
        margin="none"
        size="small"
        sx={{
            ...UNDERLINE_FIELD_SX,
            ...(props.sx as object),
        }}
    />
);

export const CompactAutocompleteInput = (props: React.ComponentProps<typeof AutocompleteInput>) => (
    <AutocompleteInput
        {...props}
        variant="standard"
        margin="none"
        size="small"
        sx={{
            ...UNDERLINE_FIELD_SX,
            width: '100%',
            maxWidth: '100%',
            '& .MuiFormControl-root': { width: '100%', maxWidth: '100%' },
            '& .MuiTextField-root': { width: '100%', maxWidth: '100%' },
            '& .MuiAutocomplete-inputRoot': { minHeight: 30, py: '2px' },
            '& .MuiAutocomplete-input': { py: '5px !important' },
            ...(props.sx as object),
        }}
    />
);

/**
 * Measures a document title's rendered width so a sibling field-label column can align to it
 * (Odoo-style document header: labels right-align flush with the title above them).
 */
export function useDocumentTitleInputStart(displayText: string) {
    const titleRef = React.useRef<HTMLElement | null>(null);
    const [startPx, setStartPx] = React.useState(120);

    React.useLayoutEffect(() => {
        const el = titleRef.current;
        if (!el) return;
        const update = () => setStartPx(Math.ceil(el.getBoundingClientRect().width) + 2);
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        window.addEventListener('resize', update);
        return () => {
            ro.disconnect();
            window.removeEventListener('resize', update);
        };
    }, [displayText]);

    return { titleRef, startPx };
}

const DOCUMENT_FIELD_ROW_SX = {
    display: 'grid',
    columnGap: '8px',
    rowGap: '2px',
    alignItems: 'center',
    minHeight: 28,
    mb: '2px',
} as const;

const DOCUMENT_FIELD_LABEL_SX = {
    ...FIELD_LABEL_SX,
    fontSize: '0.78rem',
    lineHeight: 1.3,
    minWidth: 0,
    pr: 1,
    whiteSpace: 'nowrap',
} as const;

const DOCUMENT_FIELD_VALUE_SX = {
    ...FIELD_VALUE_SX,
    minWidth: 0,
    width: '100%',
    lineHeight: '26px',
    '& .RaReferenceInput-root': { width: '100%', display: 'block' },
    '& .ra-input': { width: '100%', maxWidth: '100%' },
    '& .MuiFormControl-root': { width: '100%', maxWidth: '100%' },
    '& .MuiAutocomplete-root': { width: '100%', maxWidth: '100%' },
    '& .MuiTextField-root': { width: '100%', maxWidth: '100%' },
} as const;

/** Document header field row: label right-aligned to a dynamically measured title-width column (see {@link useDocumentTitleInputStart}). */
export function DocumentFieldRow({
    label,
    children,
    labelColPx,
}: {
    label: React.ReactNode;
    children: React.ReactNode;
    labelColPx: number;
}) {
    return (
        <Box
            sx={{
                ...DOCUMENT_FIELD_ROW_SX,
                gridTemplateColumns: { xs: 'auto minmax(0, 1fr)', sm: `${labelColPx}px minmax(0, 1fr)` },
            }}
        >
            <Typography component="div" sx={{ ...DOCUMENT_FIELD_LABEL_SX, textAlign: { xs: 'left', sm: 'right' } }}>
                {label}
            </Typography>
            <Box sx={DOCUMENT_FIELD_VALUE_SX}>{children}</Box>
        </Box>
    );
}

export const UNDERLINE_FIELD_WITH_HELPER_SX = {
    mb: 0,
    '& .MuiInputBase-root': { fontSize: '0.8rem', minHeight: 30 },
    '& .MuiInputBase-input': { py: '5px' },
    '& .MuiInputLabel-root': { display: 'none' },
    '& .MuiFormHelperText-root': {
        fontSize: '0.7rem',
        mt: 0.5,
        mx: 0,
    },
};
